import {
  Box,
  Paper,
  Step,
  StepContent,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useState } from "react";
import CommonButton from "components/common/ui/CommonButton";
import styled, { css } from "styled-components";
import { Check, Circle } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useAppSelector } from "hooks/useReduxStore";
import menusInWeekSelector from "store/selectors/menu/menusInWeekSelector";
import ChooseDateRangeOrALaCarteStep from "./cyclePurchaseSteps/ChooseDateRangeOrALaCarteStep";
import ChooseDateRangeStep from "./cyclePurchaseSteps/ChooseDateRangeStep";
import SelectMenusStep from "./cyclePurchaseSteps/SelectMenusStep";

const steps = [
  {
    label: "Daily menus or A-La-Carte",
    description: `Select the date range for which you want to generate the 
              ingredient purchase list.`,
  },
  {
    label: "Select date range",
    description: `Select the date range for which you want to generate the 
              ingredient purchase list.`,
  },
  {
    label: "Select menus",
    description: "Click on menu to select and compute require ingredients.",
  },
];

interface QontoStepIconRootProps {
  active?: boolean;
  isDarkMode?: boolean; // optional, if you still want to control dark mode manually
}

export const QontoStepIconRoot = styled.div.withConfig({
  shouldForwardProp: (prop) => !["active", "isDarkMode"].includes(prop),
})<QontoStepIconRootProps>`
  display: flex;
  height: 22px;
  align-items: center;

  color: ${({ isDarkMode }) =>
    isDarkMode ? "#9e9e9e" /* grey[700] */ : "#eaeaf0"};

  ${({ active }) =>
    active &&
    css`
      color: rgb(120, 203, 255);
    `}

  .QontoStepIcon-completedIcon {
    color: rgb(120, 203, 255);
    border-radius: 50%;
    z-index: 1;
  }

  .QontoStepIcon-circle {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

const QontoStepIcon = (props: StepIconProps) => {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot active={active} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <Circle />
      )}
    </QontoStepIconRoot>
  );
};

const CycleFoodPurchaseStepper = () => {
  const menuInWeekType = useAppSelector(
    menusInWeekSelector.menusInWeekMenuType,
  );
  const isALaCarteMenu = menuInWeekType === "alternate menu";
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    from: Dayjs;
    to: Dayjs;
  } | null>(null);

  const handleNext =
    (increment: number = 1) =>
    () => {
      setActiveStep((prevActiveStep) => prevActiveStep + increment);
    };

  const handleBack =
    (decrement: number = 1) =>
    () => {
      setActiveStep((prevActiveStep) => prevActiveStep - decrement);
    };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedDateRange(null);
  };

  const handleDateRangeSelected = (from: Dayjs, to: Dayjs) => {
    setSelectedDateRange({ from, to });
    setActiveStep((prev) => prev + 1);
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step key="chose-date-range-or-a-la-carte">
          <StepLabel StepIconComponent={QontoStepIcon}>
            Choose Date Range or A-La-Carte
          </StepLabel>
          <StepContent>
            <ChooseDateRangeOrALaCarteStep handleNext={handleNext} />
          </StepContent>
        </Step>
        <Step key="choose-date-range">
          <StepLabel StepIconComponent={QontoStepIcon}>
            Select date range
          </StepLabel>
          <StepContent>
            <ChooseDateRangeStep
              onNext={handleDateRangeSelected}
              onBack={handleBack()}
            />
          </StepContent>
        </Step>
        <Step key="select-menus">
          <StepLabel StepIconComponent={QontoStepIcon}>Select menus</StepLabel>
          <StepContent>
            {(selectedDateRange || isALaCarteMenu) && (
              <SelectMenusStep
                onNext={() => setActiveStep((prev) => prev + 1)}
                onBack={handleBack(isALaCarteMenu ? 2 : 1)}
                fromDate={selectedDateRange?.from || dayjs()}
                toDate={selectedDateRange?.to || dayjs()}
              />
            )}
          </StepContent>
        </Step>
      </Stepper>
      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography>
            All steps completed - want to get another purchase list?
          </Typography>
          <CommonButton text="Restart" onClick={handleReset} />
        </Paper>
      )}
    </Box>
  );
};

export default CycleFoodPurchaseStepper;
